/**
 * Pure text-fitting rules for the visitor name and the monogram fallback.
 * Kept free of canvas APIs so they can be unit-tested; the theme passes in a
 * `measure` callback backed by `ctx.measureText`.
 */

export type MeasureText = (text: string, fontSize: number) => number;

export interface BadgeNameOptions {
  maxWidth: number;
  /** Size used for short names, and the one used once the name is long. */
  fontSizes: { large: number; small: number };
  measure: MeasureText;
  maxLines?: number;
}

/** Names longer than this many characters always use the small size (spec rule). */
const LONG_NAME_THRESHOLD = 22;

export interface BadgeNameLayout {
  fontSize: number;
  lines: string[];
}

const ELLIPSIS = "…";

/** Cut `text` with an ellipsis until it fits `maxWidth` at `fontSize`. */
export const truncateToWidth = (
  text: string,
  fontSize: number,
  maxWidth: number,
  measure: MeasureText
): string => {
  if (measure(text, fontSize) <= maxWidth) return text;
  let end = text.length;
  while (end > 0) {
    end -= 1;
    const candidate = `${text.slice(0, end).trimEnd()}${ELLIPSIS}`;
    if (measure(candidate, fontSize) <= maxWidth) return candidate;
  }
  return ELLIPSIS;
};

const wrapWords = (
  words: string[],
  fontSize: number,
  maxWidth: number,
  measure: MeasureText
): string[] => {
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (!current || measure(candidate, fontSize) <= maxWidth) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = word;
  }
  if (current) lines.push(current);
  return lines;
};

/**
 * Fit the name in at most `maxLines` lines. The large size is used only when
 * the whole name fits on one line; otherwise the name steps down one size
 * and may wrap. When even that does not fit, the tail is truncated with an
 * ellipsis so the surname is what gets cut, never the given name.
 */
export const layoutBadgeName = (
  name: string,
  options: BadgeNameOptions
): BadgeNameLayout => {
  const { maxWidth, fontSizes, measure, maxLines = 2 } = options;
  const trimmed = name.trim().replace(/\s+/g, " ");
  if (!trimmed) return { fontSize: fontSizes.large, lines: [] };

  const words = trimmed.split(" ");
  const fitsLarge =
    trimmed.length <= LONG_NAME_THRESHOLD &&
    measure(trimmed, fontSizes.large) <= maxWidth;
  const fontSize = fitsLarge ? fontSizes.large : fontSizes.small;

  const wrapped = wrapWords(words, fontSize, maxWidth, measure);
  const kept = wrapped.slice(0, maxLines);
  if (wrapped.length > maxLines) {
    const overflow = wrapped.slice(maxLines - 1).join(" ");
    kept[maxLines - 1] = overflow;
  }

  return {
    fontSize,
    lines: kept.map((line) =>
      truncateToWidth(line, fontSize, maxWidth, measure)
    )
  };
};

/** Up to two initials, from the first and the last word of the name. */
export const getBadgeInitials = (name: string): string => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => /\p{L}|\p{N}/u.test(word));
  if (words.length === 0) return "";
  const first = words[0];
  const last = words[words.length - 1];
  const initialOf = (word: string): string => {
    const match = word.match(/\p{L}|\p{N}/u);
    return match ? match[0].toUpperCase() : "";
  };
  return words.length === 1
    ? initialOf(first)
    : `${initialOf(first)}${initialOf(last)}`;
};
